import React from 'react';
import { TrackedLink, TrackedAnchor } from '@components/analytics/TrackedLink';
import { useTheme } from '@theme/useTheme';
import { FaFacebook, FaTwitter, FaLinkedin, FaInstagram, FaYoutube, FaReddit, FaPinterest, FaWhatsapp, FaPhone, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import { FaQuora } from 'react-icons/fa6';

const Footer: React.FC = () => {
  const { colors } = useTheme();

  const currentYear = new Date().getFullYear();

  return (
    <footer
      className="mt-auto"
      style={{
        backgroundColor: colors.primary,
        color: colors.background
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {/* Company Info */}
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center mb-4">
              <img
                src="/logo.png"
                alt="Sleep Apnea Assessment Logo"
                className="h-8 w-auto"
              />
              <span className="ml-3 text-xl font-bold text-white">
                Sleep Apnea Assessment
              </span>
            </div>
            <p className="mb-4 max-w-md text-white">
              Your trusted partner in sleep health assessment. We help you understand
              your sleep patterns and provide personalized recommendations for better sleep quality.
            </p>
            <div className="flex flex-wrap gap-3">
              <TrackedAnchor
                href="https://www.instagram.com/vitals7care?igsh=MThzc2szbDZobnF1eg=="
                target="_blank"
                rel="noopener noreferrer"
                className="transition-colors duration-200 hover:opacity-70 text-white"
                aria-label="Instagram"
                gtmId="footer_social_instagram"
                gtmSection="footer_social"
                gtmLabel="Instagram"
              >
                <FaInstagram size={20} />
              </TrackedAnchor>
              <TrackedAnchor
                href="http://facebook.com/profile.php?id=61576419452994"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-colors duration-200 hover:opacity-70 text-white"
                aria-label="Facebook"
                gtmId="footer_social_facebook"
                gtmSection="footer_social"
                gtmLabel="Facebook"
              >
                <FaFacebook size={20} />
              </TrackedAnchor>
              <TrackedAnchor
                href="https://youtube.com/@vitals7care?si=3G1h9d8VXNljVNNY"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-colors duration-200 hover:opacity-70 text-white"
                aria-label="YouTube"
                gtmId="footer_social_youtube"
                gtmSection="footer_social"
                gtmLabel="YouTube"
              >
                <FaYoutube size={20} />
              </TrackedAnchor>
              <TrackedAnchor
                href="https://www.reddit.com/user/Vitals7cares/?utm_source=share&utm_medium=web3x&utm_name=web3xcss&utm_term=1&utm_content=share_button"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-colors duration-200 hover:opacity-70 text-white"
                aria-label="Reddit"
                gtmId="footer_social_reddit"
                gtmSection="footer_social"
                gtmLabel="Reddit"
              >
                <FaReddit size={20} />
              </TrackedAnchor>
              <TrackedAnchor
                href="https://www.quora.com/profile/Vitals7cares"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-colors duration-200 hover:opacity-70 text-white"
                aria-label="Quora"
                gtmId="footer_social_quora"
                gtmSection="footer_social"
                gtmLabel="Quora"
              >
                <FaQuora size={20} />
              </TrackedAnchor>
              <TrackedAnchor
                href="https://pin.it/3kUWPM0od"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-colors duration-200 hover:opacity-70 text-white"
                aria-label="Pinterest"
                gtmId="footer_social_pinterest"
                gtmSection="footer_social"
                gtmLabel="Pinterest"
              >
                <FaPinterest size={20} />
              </TrackedAnchor>
              <TrackedAnchor
                href="https://www.linkedin.com/in/vitals7-bring-health-home-69824823b/?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=android_app"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-colors duration-200 hover:opacity-70 text-white"
                aria-label="LinkedIn"
                gtmId="footer_social_linkedin"
                gtmSection="footer_social"
                gtmLabel="LinkedIn"
              >
                <FaLinkedin size={20} />
              </TrackedAnchor>
              <TrackedAnchor
                href="https://x.com/Vitals7care"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-colors duration-200 hover:opacity-70 text-white"
                aria-label="X (Twitter)"
                gtmId="footer_social_x"
                gtmSection="footer_social"
                gtmLabel="X (Twitter)"
              >
                <FaTwitter size={20} />
              </TrackedAnchor>
              <TrackedAnchor
                href="https://whatsapp.com/channel/0029Vb5vjEq11ulPY3bPDd24"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-colors duration-200 hover:opacity-70 text-white"
                aria-label="WhatsApp"
                gtmId="footer_social_whatsapp"
                gtmSection="footer_social"
                gtmLabel="WhatsApp"
              >
                <FaWhatsapp size={20} />
              </TrackedAnchor>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold mb-3">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <TrackedLink
                  to="/"
                  className="transition-colors duration-200 hover:opacity-70 text-white"
                  gtmId="footer_nav_home"
                  gtmSection="footer_quick_links"
                  gtmLabel="Home"
                >
                  Home
                </TrackedLink>
              </li>
              <li>
                <TrackedLink
                  to="/about"
                  className="transition-colors duration-200 hover:opacity-70 text-white"
                  gtmId="footer_nav_about"
                  gtmSection="footer_quick_links"
                  gtmLabel="About Sleep Apnea"
                >
                  About Sleep Apnea
                </TrackedLink>
              </li>
              <li>
                <TrackedLink
                  to="/assessment"
                  className="transition-colors duration-200 hover:opacity-70 text-white"
                  gtmId="footer_nav_assessment"
                  gtmSection="footer_quick_links"
                  gtmLabel="Take Assessment"
                >
                  Take Assessment
                </TrackedLink>
              </li>
              <li>
                <TrackedLink
                  to="/results"
                  className="transition-colors duration-200 hover:opacity-70 text-white"
                  gtmId="footer_nav_results"
                  gtmSection="footer_quick_links"
                  gtmLabel="View Results"
                >
                  View Results
                </TrackedLink>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-white font-semibold mb-3">Contact Us</h3>
            <ul className="space-y-2">
              <li className="flex items-center gap-2">
                <FaPhone size={14} className="text-white" />
                <span className="text-sm text-white">
                  +1 (317) 201-8395
                </span>
              </li>
              <li className="flex items-center gap-2">
                <FaEnvelope size={14} className="text-white" />
                <TrackedAnchor
                  href="mailto:info@vitals7.com"
                  className="transition-colors duration-200 hover:opacity-70 text-sm text-white"
                  gtmId="footer_contact_email_info"
                  gtmSection="footer_contact"
                  gtmLabel="info@vitals7.com"
                >
                  info@vitals7.com
                </TrackedAnchor>
              </li>
              <li className="flex items-center gap-2">
                <FaEnvelope size={14} className="text-white" />
                <TrackedAnchor
                  href="mailto:vinnie.rao@gmail.com"
                  className="transition-colors duration-200 hover:opacity-70 text-sm text-white"
                  gtmId="footer_contact_email_vinnie"
                  gtmSection="footer_contact"
                  gtmLabel="vinnie.rao@gmail.com"
                >
                  vinnie.rao@gmail.com
                </TrackedAnchor>
              </li>
              <li className="flex items-center gap-2">
                <FaMapMarkerAlt size={14} className="text-white" />
                <span className="text-sm text-white">
                  Roslyn Heights, NY 11577
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div
          className="border-t border-white/20 mt-4 pt-4 flex flex-col sm:flex-row justify-center items-center"
        >
          <p className="text-sm text-center text-white">
            © {currentYear} Vitals7. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
